import transServiceV2 from "../services/transServiceV2";

/**
 * ============================================================================
 * Patient Files API
 * EHR-chat file-storage replacement APIs using clinicId + patientId
 * Base path: /files
 * Base URL (production): https://api.vibrant-america.com/v2/portal/trans-service
 * Base URL (staging): https://api.vibrant-america.com/v2/portal/trans-service-st
 * Documentation: https://vibrantamerica.atlassian.net/wiki/spaces/~71202017c40220be394183b2c01fb64b143d52/pages/2274164739
 * ============================================================================
 */

/**
 * Upload a patient file
 * @param {FormData} formData - Form data with required fields
 * @param {string} formData.clinic_id - Clinic ID (required)
 * @param {string} formData.patient_id - Patient ID (required)
 * @param {string} formData.source - Source: 'chart', 'patient_upload', 'provider_upload' (optional)
 * @param {string} formData.type - Type: 'uploaded_file', 'returned_form' (optional)
 * @param {string} formData.external_url - External URL for returned forms (optional)
 * @param {File} formData.file - File to upload (required unless type=returned_form)
 * @returns {Promise} API response with file metadata
 * 
 * Response shape:
 * {
 *   id: "uuid",
 *   fileName: "lab-result.pdf",
 *   fileSize: 12345,
 *   contentType: "application/pdf",
 *   uploadedBy: "33001",
 *   uploadedByType: "customer",
 *   source: "provider_upload",
 *   type: "uploaded_file",
 *   createdAt: "2026-03-10T10:00:00.000Z",
 *   title: "lab-result.pdf",
 *   description: null,
 *   patientId: "20001",
 *   externalUrl: null
 * }
 * 
 * Allowed MIME types:
 * - image/jpeg, image/png, image/gif
 * - application/pdf
 * - text/plain
 * - application/msword
 * - application/vnd.openxmlformats-officedocument.wordprocessingml.document
 * - application/vnd.ms-excel
 * - application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
 * 
 * Max file size: 10MB
 */
export function uploadPatientFileApi(formData) {
  return transServiceV2({
    url: "/files/upload",
    method: "post",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
}

/**
 * List patient files and reports in one flattened array
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @param {Object} params - Query parameters
 * @param {string} params.source - Filter by source: 'chart', 'patient_upload', 'provider_upload' (optional)
 * @returns {Promise} API response with files and reports array
 * 
 * Response includes:
 * - Uploaded files with type = "uploaded_file"
 * - Report entries with type = "report"
 * 
 * Report shape:
 * {
 *   accessionId: "ACC-10001",
 *   createdAt: "2026-03-10T10:00:00.000Z",
 *   type: "report"
 * }
 */
export function getPatientFilesApi(clinicId, patientId, params = {}) {
  return transServiceV2({
    url: `/files/clinic/${clinicId}/patient/${patientId}`,
    method: "get",
    params
  });
}

/**
 * Download file content by id
 * @param {string} id - File ID
 * @returns {Promise} API response with file blob
 * 
 * Notes:
 * - Returns file bytes directly
 * - Despite the path name "decrypt", current implementation does not perform decryption
 * - Only works for patient-scoped files
 * - For file download/content retrieval, not metadata
 * - URL-only returned forms do not have downloadable blob content
 */
export function downloadPatientFileApi(id) {
  return transServiceV2({
    url: `/files/decrypt/${id}`,
    method: "get",
    responseType: "blob"
  });
}

/**
 * Get patient file metadata by id
 * @param {string} id - File ID
 * @returns {Promise} API response with file metadata (JSON)
 * 
 * Notes:
 * - Only works for patient-scoped files
 * - Clinic-level documents are not exposed here
 * - Returns JSON metadata only, not file bytes
 * - For returned forms, this returns the stored URL metadata
 */
export function getPatientFileMetadataApi(id) {
  return transServiceV2({
    url: `/files/${id}`,
    method: "get"
  });
}

/**
 * Delete one patient file by id
 * @param {string} id - File ID
 * @returns {Promise} API response
 */
export function deletePatientFileApi(id) {
  return transServiceV2({
    url: `/files/${id}`,
    method: "delete"
  });
}

/**
 * Delete all stored patient files for a clinic/patient pair
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @returns {Promise} API response
 * 
 * Notes:
 * - Deletes file records from MySQL and file binaries from Azure Blob Storage
 * - Report entries are derived from gRPC and are not deleted by this endpoint
 */
export function deleteAllPatientFilesApi(clinicId, patientId) {
  return transServiceV2({
    url: `/files/clinic/${clinicId}/patient/${patientId}`,
    method: "delete"
  });
}

/**
 * ============================================================================
 * Helper Functions
 * ============================================================================
 */

/**
 * Create FormData for uploading a patient file
 * @param {Object} params - Upload parameters
 * @param {string} params.clinicId - Clinic ID (required)
 * @param {string} params.patientId - Patient ID (required)
 * @param {File} params.file - File to upload (required unless type=returned_form)
 * @param {string} params.source - Source: 'chart', 'patient_upload', 'provider_upload' (optional)
 * @param {string} params.type - Type: 'uploaded_file', 'returned_form' (optional)
 * @param {string} params.externalUrl - External URL for returned forms (optional)
 * @returns {FormData} FormData object ready for upload
 */
export function createPatientFileFormData({ clinicId, patientId, file, source, type, externalUrl }) {
  const formData = new FormData();
  
  formData.append('clinic_id', clinicId);
  formData.append('patient_id', patientId);
  
  if (file) {
    formData.append('file', file);
  }
  
  if (source) {
    formData.append('source', source);
  }
  
  if (type) {
    formData.append('type', type);
  }
  
  if (externalUrl) {
    formData.append('external_url', externalUrl);
  }
  
  return formData;
}

/**
 * Create FormData for uploading a returned form (URL-only document)
 * @param {Object} params - Upload parameters
 * @param {string} params.clinicId - Clinic ID (required)
 * @param {string} params.patientId - Patient ID (required)
 * @param {string} params.externalUrl - External URL (required)
 * @param {string} params.source - Source: 'chart', 'patient_upload', 'provider_upload' (optional)
 * @returns {FormData} FormData object ready for upload
 */
export function createReturnedFormFormData({ clinicId, patientId, externalUrl, source }) {
  const formData = new FormData();
  
  formData.append('clinic_id', clinicId);
  formData.append('patient_id', patientId);
  formData.append('type', 'returned_form');
  formData.append('external_url', externalUrl);
  
  if (source) {
    formData.append('source', source);
  }
  
  return formData;
}

/**
 * ============================================================================
 * Patient Chat Images API
 * Base path: /api/v1/patient-chat/images
 * ============================================================================
 */

/**
 * Batch upload chat images for clinic_id + patient_id
 * @param {FormData} formData - Form data with required fields
 * @param {string} formData.clinic_id - Clinic ID (required)
 * @param {string} formData.patient_id - Patient ID (required)
 * @param {File[]} formData.files - Multiple image files (required)
 * @returns {Promise} API response with uploaded images metadata
 * 
 * Response shape:
 * {
 *   items: [
 *     {
 *       id: "6f4fe5c2-5da0-40bc-8d66-a8f0726ea549",
 *       clinic_id: "10136",
 *       patient_id: "20001",
 *       storage_key: "chat-images/10136/patient/20001/6f4fe5c2-5da0-40bc-8d66-a8f0726ea549-photo.png",
 *       file_name: "photo.png",
 *       mime_type: "image/png",
 *       size_bytes: 123456,
 *       created_at: "2026-03-10T10:00:00.000Z",
 *       uploaded_by: "33001",
 *       uploaded_by_type: "customer"
 *     }
 *   ]
 * }
 * 
 * Allowed MIME types:
 * - image/jpeg
 * - image/png
 * - image/gif
 * - image/webp
 */
export function batchUploadChatImagesApi(formData) {
  return transServiceV2({
    url: "/api/v1/patient-chat/images/batch-upload",
    method: "post",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
}

/**
 * Get chat image metadata by ids within one clinic_id + patient_id scope
 * @param {Object} data - Request body
 * @param {string} data.clinic_id - Clinic ID (required)
 * @param {string} data.patient_id - Patient ID (required)
 * @param {string[]} data.ids - Array of image IDs (required)
 * @returns {Promise} API response with images metadata
 * 
 * Response shape: Same as batch-upload response
 */
export function batchGetChatImagesApi(data) {
  return transServiceV2({
    url: "/api/v1/patient-chat/images/batch-get",
    method: "post",
    data
  });
}

/**
 * Get one chat image metadata by id
 * @param {string} id - Image ID
 * @returns {Promise} API response with image metadata
 * 
 * Response shape: Same object shape as one items[] entry from batch-upload
 */
export function getChatImageApi(id) {
  return transServiceV2({
    url: `/api/v1/patient-chat/images/${id}`,
    method: "get"
  });
}

/**
 * Delete one chat image
 * @param {string} id - Image ID
 * @returns {Promise} API response (HTTP 204 No Content)
 */
export function deleteChatImageApi(id) {
  return transServiceV2({
    url: `/api/v1/patient-chat/images/${id}`,
    method: "delete"
  });
}

/**
 * ============================================================================
 * Helper Functions for Chat Images
 * ============================================================================
 */

/**
 * Create FormData for batch uploading chat images
 * @param {Object} params - Upload parameters
 * @param {string} params.clinicId - Clinic ID (required)
 * @param {string} params.patientId - Patient ID (required)
 * @param {File[]} params.files - Array of image files (required)
 * @returns {FormData} FormData object ready for upload
 */
export function createChatImagesFormData({ clinicId, patientId, files }) {
  const formData = new FormData();
  
  formData.append('clinic_id', clinicId);
  formData.append('patient_id', patientId);
  
  // Append multiple files
  files.forEach(file => {
    formData.append('files', file);
  });
  
  return formData;
}

/**
 * ============================================================================
 * Clinic Documents API
 * ============================================================================
 */

/**
 * Get clinic documents list
 * @param {Object} params - Query parameters
 * @param {string} params.clinic_id - Clinic ID (required)
 * @param {string} params.patient_id - Patient ID (optional)
 * @param {string} params.source - Source filter: 'chart', 'patient_upload', 'provider_upload' (optional)
 * @param {string} params.type - Type filter: 'uploaded_file', 'returned_form' (optional)
 * @param {string} params.search - Search term (optional)
 * @param {number} params.offset - Pagination offset (optional, default 0)
 * @param {number} params.limit - Pagination limit (optional, default 20)
 * @returns {Promise} API response with documents list
 * 
 * Response shape:
 * {
 *   items: [
 *     {
 *       id: "uuid",
 *       clinic_id: "10136",
 *       patient_id: "20001",
 *       title: "Progress Note",
 *       description: "Imported from chart",
 *       file_name: "note.pdf",
 *       mime_type: "application/pdf",
 *       size_bytes: 12345,
 *       download_url: "https://...",
 *       external_url: "https://...",
 *       type: "returned_form",
 *       source: "chart",
 *       uploaded_by: "33001",
 *       uploaded_by_type: "customer",
 *       created_at: "2026-03-10T10:00:00.000Z",
 *       updated_at: "2026-03-10T10:00:00.000Z"
 *     }
 *   ],
 *   count: 1
 * }
 * 
 * Notes:
 * - When patient_id is provided, returns patient files from shared storage table
 * - count is the number of returned items in the current page
 */
export function getClinicDocumentsApi(params) {
  return transServiceV2({
    url: "/api/v1/clinic-charting/documents",
    method: "get",
    params: params
  });
}

/**
 * Get a single clinic chart document by ID
 * @param {string} documentId - Document ID to fetch
 */
export function getClinicDocumentByIdApi(documentId) {
  return transServiceV2({
    url: `/api/v1/clinic-charting/documents/${documentId}`,
    method: "get"
  });
}

/**
 * Delete a clinic document
 * @param {string} documentId - Document ID to delete
 */
export function deleteClinicDocumentApi(documentId) {
  return transServiceV2({
    url: `/api/v1/clinic-charting/documents/${documentId}`,
    method: "delete"
  });
}
